import ChainOfThoughtModel from "model/ChainOfThought.model";
import moment, { Moment } from "moment";
import { MessageContentType, possibleMomentDateFormats, Rating } from "./CommonConstant";
import { FILE_STATUS } from "./Constant";

export const getEnvVariableValue = (key: any) => {
  if (!key) throw new Error("Please pass the key to get value");
  if (window?.env?.[key]) return window?.env?.[key];

  return import.meta.env[key];
};

/**
 *
 * @param {any} data
 * @returns encoded string
 */
export const encode = (data: string) => {
  return btoa(data);
};

/**
 *
 * @param {string} data // encrypted data
 * @returns decoded string
 */
export const decode = (data: string) => {
  return atob(data);
};

export const getLocalLanguage = () => {
  const localStorageObject = JSON.parse(getLocalStorage("defaultLocale"));
  return localStorageObject.defaultLanguage;
};

/**
 *
 * @param {string} data
 */
export const copyData = async (data: string, callback?: any): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(data);
    console.log(">> Copied!!");
    callback?.();
    return true;
  } catch (error) {
    console.log(">> Fail to copy");
    return false;
  }
};

/**
 * Retrieves the text from the clipboard.
 * @returns The text from the clipboard, or undefined if the Clipboard API is not available or there was an error reading the clipboard contents.
 */
export const pasteFromClipboard = async (): Promise<string | undefined> => {
  if (!navigator.clipboard) {
    console.error("Clipboard API not available");
    return;
  }

  try {
    return await navigator.clipboard.readText();
  } catch (err) {
    console.error("Failed to read clipboard contents: ", err);
  }
};

/**
 *
 * @param {string} key
 * read data from local storage
 */
export const getLocalStorage = (key: string) => {
  return localStorage.getItem(key) ?? "";
};
/**
 *
 * @param {string} key
 * @param {string | object} value
 */
export const setLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

/**
 *
 * @param {string} key
 */
export const removeLocalStorage = (key: string) => {
  localStorage.removeItem(key);
};

/**
 *
 * @returns boolean value of the network status
 */
export const isOnline = () => {
  return navigator.onLine;
};

// multiple date formats
function addZero(i: number | string) {
  if (typeof i === "number" && i < 10) {
    i = "0" + i;
  }
  return i;
}

/**
 *
 * @param {integer} month
 * @returns previous date based on parameter passed or it default return on month back date
 */
export const getPrevMonthDate = (month = 1) => {
  const prevDate = new Date();
  prevDate.setMonth(prevDate.getMonth() - month);
  return prevDate;
};
/**
 *
 * @param {Date | string} date
 * @returns formatted Time With AM / PM
 */
export const formatTimeIn12HourFormat = (dateInString: string): string => {
  const date = new Date(dateInString);
  let hours: number = date.getHours();
  let minutes: number | string = date.getMinutes();
  const ampm: string = hours >= 12 ? "pm" : "am";

  // Convert hours to 12-hour format
  hours = hours % 12 || 12;

  // Add leading zero to minutes if needed
  minutes = minutes < 10 ? "0" + minutes : minutes;

  return `${hours}:${minutes} ${ampm}`;
};

/**
 *
 * @param {string} dateToFormat
 * @returns  formatted string from date object
 */
export const formattedDate = (dateToFormat: string): string => {
  if (!dateToFormat) return "";

  const formatWithLeadingZero = (num: number): string => (num < 10 ? "0" + num : String(num));

  const myDate = new Date(dateToFormat);
  const month: string = formatWithLeadingZero(myDate.getMonth() + 1);
  const date: string = formatWithLeadingZero(myDate.getDate());
  const year: number = myDate.getFullYear();
  const hh: string = formatWithLeadingZero(myDate.getHours());
  const mm: string = formatWithLeadingZero(myDate.getMinutes());
  const ss: string = formatWithLeadingZero(myDate.getSeconds());

  return `${date}/${month}/${year} ${hh}:${mm}:${ss}`;
};

// ---- Standard Validator ----
/**
 * Standard Validator functions to be used with simple-react-validator package
 * @returns validation object
 */
export const getStandardValidator = () => {
  return {
    mandatory: {
      message: "Mandatory",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (val.toString()) {
          return true;
        } else {
          return false;
        }
      },
      required: true, // optional
    },
    email: {
      message: "Invalid email address",
      rule: (val: any, params?: any, validator?: any): boolean => {
        const re = /\S+@\S+\.\S+/;
        return re.test(val);
      },
    },
    onlyPositive: {
      message: "Negative values are not allowed",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (val) {
          const isPositive = Math.sign(Number(val));
          if (isPositive < 0) {
            return false;
          }
          return true;
        } else {
          return true;
        }
      },
    },
    onlyInteger: {
      message: "Decimals not allowed",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (val) {
          const numericValue = Number(val);
          if (!(numericValue || numericValue === 0) && !isNaN(numericValue)) {
            return false;
          }
          const splitValue = val.toString().split(".");
          if (splitValue) {
            if (splitValue.length > 1) {
              return false;
            }
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      },
    },
    max: {
      message: ":type",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (params[1] === "num") {
          if (parseFloat(val) > parseFloat(params[0])) {
            return false;
          } else {
            return true;
          }
        } else if (val.length > parseFloat(params[0])) {
          return false;
        } else {
          return true;
        }
      },
      messageReplace: (message: string, params?: any) => {
        if (params[1] === "num") {
          return message.replace(":type", `Max value is ${params[0]}`);
        } else {
          return message.replace(":type", `Max length ${params[0]} chars`);
        }
      },
    },
    min: {
      message: ":type",
      rule: (val: any, params?: any, validator?: any): boolean => {
        if (params[1] === "num") {
          if (parseFloat(val) < parseFloat(params[0])) {
            return false;
          } else {
            return true;
          }
        } else if (val.length < parseFloat(params[0])) {
          return false;
        } else {
          return true;
        }
      },
      messageReplace: (message: string, params?: any) => {
        if (params[1] === "num") {
          return message.replace(":type", `Min value is ${params[0]}`);
        } else {
          return message.replace(":type", `Min length ${params[0]} chars`);
        }
      },
    },
    maxDecimal: {
      message: "Enter only Four Decimal values",
      rule: (val: any, params?: any, validator?: any): boolean => {
        const re = new RegExp(`^\\d+\\.?\\d{0,${params[0]}}$`);

        if (re.test(val)) {
          return true;
        } else {
          return false;
        }
      },
    },
    passwordMatch: {
      message: "Passwords do not match",
      rule: (val: any, params?: any, validator?: any): boolean => {
        return val === params[0];
      },
      messageReplace: (message: string, params: string) => message,
      required: true,
    },
    validateUrl: {
      message: "Invalid URL",
      rule: (val: any, params?: any, validator?: any): boolean => {
        const validUrlRegex = /^[a-zA-Z0-9-._~:/@!$&'()*+,;=%]+$/;
        const restrictedControlCharsRegex = /[?#]/;
        return validUrlRegex.test(val) && !restrictedControlCharsRegex.test(val);
      },
    },
    noSpace: {
      message: "Space is not allowed",
      //Should not have trailing spaces also
      rule: (val: any, params?: any, validator?: any): boolean => {
        return !val.includes(" ");
      },
    },
    noUppercase: {
      message: "No uppercase allowed",
      rule: (val: any, params?: any, validator?: any): boolean => {
        return val === val.toLowerCase();
      },
    },
    unique: {
      message: "Should be unique",
      rule: (): boolean => {
        return false;
      },
    },
    alreadyExists: {
      message: "File name already exists",
      rule: (): boolean => {
        return false;
      },
    },
    //The name should not be provided text not:context
    not: {
      message: ":text is a reserved key",
      rule: (val: any, params?: any, validator?: any): boolean => {
        return val !== params[0];
      },
      messageReplace: (message: string, params: string) => {
        return message.replace(":text", `'${params[0]}'`);
      },
    },
  };
};

/**
 * Message Replacement
 * @returns
 */
export const messageReplace = () => {
  return {
    messages: {
      alpha_num: "Special chars are not allowed",
      alpha_num_dash: "Only alnum, dash(-) and _",
      numeric: "Only numbers are allowed",
      // required: "validator.mandatory",
    },
  };
};

/**
 * parse the error message from object
 * @param {object} errorMessageObj
 * @returns formatted error string
 */

export const parseStandardAPIErrorMessage = (errorMessageObj: any) => {
  // Check if errorMessageObj is a string and return it directly
  if (typeof errorMessageObj === "string") {
    return errorMessageObj;
  }

  let prepareMessage = "";
  const errorDetails = errorMessageObj?.response?.data?.details;
  const errorMessage =
    errorMessageObj?.response?.data?.message ??
    errorMessageObj?.response?.data?.error ??
    errorMessageObj?.response?.data?.error_message ??
    errorMessageObj?.response?.data?.response?.error_message ??
    errorMessageObj?.message ??
    `Something went wrong!!!`;
  if (errorDetails) {
    if (errorDetails.length > 1) {
      for (const element of errorDetails) {
        prepareMessage = prepareMessage + element + "\n";
      }
    } else {
      prepareMessage = errorDetails.at(0);
    }
    return prepareMessage;
  }
  if (errorMessage) {
    return errorMessage;
  }
};

export function validateGetAPIResponse(res: any) {
  if (res?.status === 200 && res.data) return true;
  throw new Error();
}

export function validatePostAPIResponse(res: any) {
  if (res?.status === 201 && res.data) return true;
  throw new Error();
}
export function validatePutAPIResponse(res: any) {
  if (res?.status === 200 && res.data) return true;
  throw new Error();
}

export function logWithColor(message: string, color: any) {
  console.log(`%c ${message}`, `color:${color};font-weight:bolder;font-size:16px`);
}

export function deepCopy(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

export const attachRadialGradientListener = (
  element: HTMLElement | null,
  isDarkMode: boolean,
  options?: {
    resetOnMouseLeave?: boolean;
    vendorPrefixes?: boolean;
  }
) => {
  console.log("attachRadialGradientListener", element, isDarkMode);
  if (!element) {
    return () => {
      console.log("element is not available");
    }; // Return an empty cleanup function if the element is not available
  }

  const startColor = isDarkMode ? "#24344d" : "#E6EDF9";
  const endColor = isDarkMode ? "#11161e" : "#FAFBFC";

  //Set the original background color
  element.style.background = "";

  const handleMouseMove = (e: MouseEvent) => {
    const rect = element.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const percentageX = (mouseX / element.clientWidth) * 100;
    const percentageY = (mouseY / element.clientHeight) * 100;

    const gradient = `radial-gradient(circle at ${percentageX}% ${percentageY}%, ${startColor} 15.88%, ${endColor} 100%)`;

    if (options?.vendorPrefixes) {
      element.style.background = `-moz-${gradient}`;
    }
    element.style.background = gradient;
  };

  const handleMouseLeave = () => {
    if (options?.resetOnMouseLeave) {
      element.style.background = "";
    }
  };

  // Don't set initial background color - let CSS classes handle it
  // element.style.background = endColor;

  element.addEventListener("mousemove", handleMouseMove);
  if (options?.resetOnMouseLeave) {
    element.addEventListener("mouseleave", handleMouseLeave);
  }

  // Return a cleanup function
  return () => {
    element.removeEventListener("mousemove", handleMouseMove);
    if (options?.resetOnMouseLeave) {
      element.removeEventListener("mouseleave", handleMouseLeave);
    }
  };
};

//-------------------------Charts---------------------------//
export const LEGENDS_POSITION = {
  TOP_LEFT: "TOP_LEFT",
  TOP_CENTER: "TOP_CENTER",
  TOP_RIGHT: "TOP_RIGHT",
  BOTTOM_LEFT: "BOTTOM_LEFT",
  BOTTOM_CENTER: "BOTTOM_CENTER",
  BOTTOM_RIGHT: "BOTTOM_RIGHT",
};

export const getLegendPositionClass = (position: string) => {
  switch (position) {
    case LEGENDS_POSITION.TOP_LEFT:
      return "flex justify-start top-8";
    case LEGENDS_POSITION.TOP_CENTER:
      return "flex justify-center top-8";
    case LEGENDS_POSITION.TOP_RIGHT:
      return "flex justify-end top-8";
    case LEGENDS_POSITION.BOTTOM_LEFT:
      return "flex justify-start bottom-0";
    case LEGENDS_POSITION.BOTTOM_CENTER:
      return "flex justify-center bottom-0";
    case LEGENDS_POSITION.BOTTOM_RIGHT:
      return "flex justify-end bottom-0";
    default:
      return "flex justify-start top-8"; // Default value when legendsPosition is undefined
  }
};

export const BAR_CHART_TYPE = {
  HORIZONTAL: "HORIZONTAL",
  MULTI_AXIS: "MULTI_AXIS",
  VERTICAL_STACKED: "VERTICAL_STACKED",
};

export const LINE_CHART_TYPE = {
  MULTI_AXIS: "MULTI_AXIS",
};

//Capitalize first letter in a string
export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

//Write a function to represent a number in a K, M, B and more formats
export const formatNumber = (num: number) => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, "") + "B";
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num;
};

//Generate regular expression from array
export function generateRegex(wordGroups: any[]) {
  // Construct the regex dynamically
  const regexString =
    "^" +
    wordGroups.map((group) => "(?=.*\\b(" + group.join("|") + ")\\b\\s*\\b)").join("") +
    "\\b";

  // Create a RegExp object with the constructed regex string
  return new RegExp(regexString, "i");
}

//Generate random ID function
export const generateRandomId = (length = 8) => {
  const characters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export const generateAlphabetRandomId = (length = 8) => {
  const characters = "abcdefghijklmnopqrstuvwxyz";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

export function extractQuestions(input: string): string[] {
  const regex = /```follow_up_suggestion\n(.*?)\n```/g;
  let match;
  const questions: string[] = [];
  while ((match = regex.exec(input)) !== null) {
    if (match[1].trim()) questions.push(match[1].trim());
  }

  return questions;
}

export function extractCodeBlockFromEnvelope(input: string): any[] {
  // Define the regex pattern to match the block starting with envelope_something and ending with ```
  const regex = /````envelope_[^\n]*\n([\s\S]*?)\n````/g;

  let match;
  const envelopes: any[] = [];
  while ((match = regex.exec(input)) !== null) {
    const envelopeName = match[0]?.split("````envelope_")[1]?.split("\n")[0];
    if (match[1].trim()) envelopes.push({ name: envelopeName, data: match[1].trim() });
  }
  return envelopes;
}

export function convertToId(str: string | number) {
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_");
}

export function generateEdges(input: any, isDarkMode?: boolean) {
  if (!input?.filter_edge) {
    return [];
  }

  return input.filter_edge.map((item: any) => {
    const isVisited = item.num_visited === 1;
    let markerColor: string;
    if (isVisited) {
      markerColor = "#3C91E2";
    } else if (isDarkMode) {
      markerColor = "#3B3E4A";
    } else {
      markerColor = "#949BA6";
    }

    return {
      id: `e-${item.source_filter.name}-${item.target_filter.name}`,
      source: item.source_filter.name,
      target: item.target_filter.name,
      label: ` ${item.label}, Visited: ${item.num_visited ?? 0} `,
      type: "smoothstep",
      animated: isVisited,
      markerEnd: {
        type: "arrowclosed",
        color: markerColor,
      },
      style: { stroke: markerColor, strokeWidth: 2 },
      zIndex: isVisited ? 1 : 0,
      envelope: item.envelope,
      visited: isVisited,
    };
  });
}

export function generateNodes(inputJson: any) {
  if (!inputJson) return [];

  const { filter: filters = [], filter_edge: filterEdges = [] } = inputJson;

  if (!Array.isArray(filters)) return [];

  return filters
    .map((_filter: any) => {
      if (!_filter?.filter) return null;

      const filter = _filter.filter;
      const node: any = {
        id: filter.uuid ?? "",
        name: filter.name ?? "",
        type: "customNode",
        data: {
          id: filter.uuid ?? "",
          label: filter.name ?? "",
          category: _filter.category ?? "",
          handles: [],
        },
        envelope: _filter.envelope,
        zIndex: 10,
        position: { x: 0, y: 0 },
        category: _filter.category ?? "",
        visited: !!filter.filter_visited,
      };

      let handleIndex = 0;
      if (Array.isArray(filterEdges)) {
        filterEdges.forEach((edge: any) => {
          if (edge?.source_filter?.uuid === filter.uuid) {
            node.data.handles.push({
              type: "source",
              position: "bottom",
              id: `${filter.uuid}-s-${handleIndex}`,
            });
            handleIndex++;
          }
          if (edge?.target_filter?.uuid === filter.uuid) {
            node.data.handles.push({
              type: "target",
              position: "top",
              id: `${filter.uuid}-t-${handleIndex}`,
            });
            handleIndex++;
          }
        });
      }

      return node;
    })
    .filter(Boolean); // Remove any null entries
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

//Return random value from enum
export function getRandomValueFromEnum(enumObject: any) {
  const values = Object.values(enumObject);
  const randomIndex = Math.floor(Math.random() * values.length);
  return values[randomIndex];
}

export function isADate(input: string): boolean {
  // First check if input matches common date patterns
  const datePattern =
    /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{4})?)?$|^\d{2}\/\d{2}\/\d{4}$/;
  if (!datePattern.test(input) || input.length > 30) return false;

  // Then validate with Date parsing
  try {
    // Handle non-standard timezone format (e.g., +0000 -> Z)
    const isoString = input.replace(/(\.\d+)?\+0000$/, "Z");
    const parsedDate = new Date(isoString);
    return !isNaN(parsedDate.getTime()) && parsedDate instanceof Date;
  } catch {
    return false;
  }
}

//Check if a string is a date or not, if true return the date as Jul 11, 2021 format
export function formatIfIsAValidDate(input: string) {
  // First validate with the same pattern as isADate util function
  const datePattern =
    /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{4})?)?$|^\d{2}\/\d{2}\/\d{4}$/;
  if (!datePattern.test(input) || input.length > 30) return input;

  try {
    // Handle non-standard timezone format
    const isoString = input.replace(/(\.\d+)?\+0000$/, "Z");
    const date = new Date(isoString);

    if (!isNaN(date.getTime()) && date instanceof Date) {
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch {
    // Fall through to return original input
  }

  return input; // Moved outside try-catch for non-exception failures
}

//Create a function to check if a string is a date time or not
export function isDateTime(input: string) {
  const date = new Date(input);
  return date instanceof Date && !isNaN(date.getTime());
}

//Get max number from an array
export function getMaxNumberFromArray(arr: number[]): number {
  return Math.max(...arr);
}

// Extract the first letter of the first and last words and convert to uppercase
export function extractFirstLetters(input: string) {
  // Remove consecutive spaces and trim the string
  const cleanedInput = input.replace(/\s+/g, " ").trim();

  const words = cleanedInput.split(" "); // Split the cleaned input string into words
  if (words.length === 0) return ""; // If there are no words, return an empty string

  const firstLetter = words[0][0].toUpperCase(); // Get the first letter of the first word
  const lastLetter = words[words.length - 1][0].toUpperCase(); // Get the first letter of the last word

  return firstLetter + lastLetter; // Concatenate and return the result
}

//Get random colors from given array of colors
export const getRandomColor = (colors: string[]) => {
  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
};

//----------Password verification------------//
export const verifyCharacterLimit = (string: string, lowerLimit: number) => {
  if (!string) return false;
  if (string.length >= lowerLimit) {
    return true;
  }
  return false;
};

export const verifyNumber = (string: string): boolean => {
  const numberPattern = /\d/;
  if (!string) return false;
  const match = numberPattern.exec(string);
  return match !== null;
};

export const verifyLowerCase = (string: string): boolean => {
  const lowercasePattern = /[a-z]/;
  if (!string) return false;
  const match = lowercasePattern.exec(string);
  return match !== null;
};

export const verifyUpperCase = (string: string): boolean => {
  const uppercasePattern = /[A-Z]/;
  if (!string) return false;
  const match = uppercasePattern.exec(string);
  return match !== null;
};

export const verifySpecialCharacter = (string: string): boolean => {
  const specialCharacterPattern = /[!@#$%^&*()]/;
  if (!string) return false;
  const match = specialCharacterPattern.exec(string);
  return match !== null;
};

export function splitMarkdownTextAndTables(markdown: string): string[] {
  //Regular expression to match any text followed by a table
  const regex = /([\s\S]*?)(\|.*\|[\s\S]*?\|.*\|)/g;
  return markdown.split(regex).filter((part) => part.trim());
}

export function parseObjectsFromString(inputString: string): object[] {
  // Regular expression to match the JSON content within the string
  const jsonRegex = /data:\s*({.*})/g;
  let match;
  const jsonArray = [];

  // Iterate through all matches and parse the JSON content
  while ((match = jsonRegex.exec(inputString)) !== null) {
    try {
      const jsonObject = JSON.parse(match[1]);
      jsonArray.push(jsonObject);
    } catch (e) {
      console.error("Failed to parse JSON:", e);
      throw Error("Error while parsing stream data.");
    }
  }

  return jsonArray;
}

export function getLastSegment(url: string): string {
  // Split the URL by '/' and filter out any empty segments
  const segments = url.split("/").filter((segment) => segment.length > 0);
  // Return the last segment
  return segments[segments.length - 1];
}

export function getEndpoint(url: string): string {
  try {
    // Create a new URL object to parse the input URL
    const parsedUrl = new URL(url);
    // Return the pathname which is the endpoint
    return parsedUrl.pathname;
  } catch (error) {
    // Handle any errors (e.g., invalid URL) and return an empty string
    console.error("Invalid URL:", error);
    return "";
  }
}

export function getStringInBraces(input: string): string {
  // Regular expression to match the part of the string inside curly braces
  const regex = /\{([^}]+)\}/;
  // Execute the regex on the input string
  const match = regex.exec(input);
  // If a match is found, return the captured group, otherwise return an empty string
  return match ? match[1] : "";
}

export function checkIfMac() {
  return navigator?.userAgent?.toUpperCase().indexOf("MAC") >= 0;
}

export function getRandomNumbersBetween(start: number, end: number) {
  return Math.floor(Math.random() * (end - start + 1) + start);
}

//Function to extract user info from idToken
export function extractUserInfoFromIdToken(idToken: string) {
  // Split the idToken by '.' to extract the payload
  const payload = idToken.split(".")[1];
  // Decode the payload from base64
  const decodedPayload = atob(payload);
  // Parse the decoded payload to get the user info
  return JSON.parse(decodedPayload);
}

// Get quantity number of random consecutive string
export function getRandomStringsByLength(list: (string | number)[], quantity: number) {
  //Sort the list based on it's length
  list = list.sort((a, b) => String(a).length - String(b).length);

  const result: (string | number)[] = [];

  //Getting start index
  const startIndex = getRandomNumbersBetween(0, list.length - quantity);

  //Get the items from startIndex to startIndex+quantity-1
  for (let i = startIndex; i < startIndex + quantity; i++) {
    result.push(list[i]);
  }

  return result;
}

export function removeHTMLAndMarkdown(content: string) {
  const replacedBr = content?.replace(/\n?<br\/?>\n?/g, "\n");
  const replacedSpaces = replacedBr?.replace(/&nbsp;/g, " ");
  const replacedHTML = replacedSpaces?.replace(/<[^>]+>/g, "");
  return replacedHTML
    .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold markdown
    .replace(/\*(.*?)\*/g, "$1") // Remove italic markdown
    .replace(/~~(.*?)~~/g, "$1") // Remove strikethrough markdown
    .replace(/!\[(.*?)\]\((.*?)\)/g, "$1") // Remove image markdown
    .replace(/#{1,6}\s(.*?)(?:\r\n|\r|\n)/g, "$1"); // Remove header markdown
}

//Parse the string to get the array (array of objects)
export function parseStringToArray(input: string) {
  try {
    return JSON.parse(input.replace(/'/g, '"'));
  } catch (error) {
    try {
      return JSON.parse(input);
    } catch (e) {
      throw new Error("Error while parsing the string to array.");
    }
  }
}

//Parse the string array to get the array of objects
export function convertStringArrayToObjectArray(input: string[]) {
  try {
    return input.map((item) => JSON.parse(item.replace(/'/g, '"')));
  } catch (error) {
    try {
      return input.map((item) => JSON.parse(item));
    } catch (e) {
      throw new Error("Error while parsing");
    }
  }
}

export function convertSessionBreakdownData(input: { item: string; rating: Rating }[]): any[] {
  try {
    return input.map((thought) => {
      return { ...JSON.parse(thought?.item?.replace(/'/g, '"')), rating: thought.rating };
    });
  } catch (error) {
    try {
      return input.map((thought) => {
        return { ...JSON.parse(thought?.item), rating: thought.rating };
      });
    } catch (e) {
      throw new Error("Error while parsing");
    }
  }
}

//Group the items based on category
//input: [{category:"category1", description:"Desc 1"}, {category:"category2", description:"Desc 2"}, {category:"category1", description:"Desc 3"}]
//output:[{category: category1, items: [{category:"category1", description:"Desc 1"},{category:"category1", description:"Desc 3"}]}, {category: category2, items: [{category:"category2", description:"Desc 2"}]}]
export function groupByCategory(input: ChainOfThoughtModel[]) {
  const groupedItems: { category: string; items: ChainOfThoughtModel[] }[] = [];

  input?.forEach?.((item) => {
    const existingGroup = groupedItems.find((group) => group.category === item.category);

    if (existingGroup) {
      existingGroup.items.push(item);
    } else {
      groupedItems.push({ category: item.category, items: [item] });
    }
  });

  return groupedItems;
}

export const generateQueryParams = (params: any) => {
  // with nested as well
  const query =
    "?" +
    Object.keys(params)
      .map((key) => {
        if (typeof params[key] === "object") {
          return Object.keys(params[key])
            .map((nestedKey) => `${key}[${nestedKey}]=${params[key][nestedKey]}`)
            .join("&");
        }
        return `${key}=${params[key]}`;
      })
      .join("&");
  return query;
};

export function isANumber(value: string): boolean {
  // Check if the value is a string
  if (typeof value !== "string") {
    return false;
  }

  if (value.length > 15) return false;

  // Regular expression to check if the string is a valid number
  const numberPattern = /^-?\d+(\.\d+)?$/;

  // Test the value against the regular expression
  return numberPattern.test(value);
}

export function isALink(value: string): boolean {
  // Check if the value is a string
  if (typeof value !== "string") {
    return false;
  }

  if (value.length > 32) return false;

  // Regular expression to check if the string is a valid URL
  const urlPattern = /^(https?:\/\/)?([\da-z.-]+\.[a-z.]{2,6})([/\w .-]*)*\/?$/;

  // Test the value against the regular expression
  return urlPattern.test(value);
}

export function isAnEmail(value: string): boolean {
  // Check if the value is a string
  if (typeof value !== "string") {
    return false;
  }

  // Regular expression to check if the string is a valid email
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Test the value against the regular expression
  return emailPattern.test(value);
}

export function validateId(id?: string): boolean {
  if (!id) return false;
  // Regular expression to match a 24-character hexadecimal string
  const hexRegex = /^[a-f0-9]{24}$/i;
  return hexRegex.test(id);
}

export const trimString = (value: any, defaultValue = "") => {
  return typeof value === "string" ? value.trim() : defaultValue;
};

export function timeDurationConverter(milliseconds: number | string): string {
  if (!milliseconds || Number(milliseconds) === 0) return "Execution took 0 milliseconds.";

  const ms = Number(milliseconds);
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / (1000 * 60)) % 60;
  const hours = Math.floor(ms / (1000 * 60 * 60)) % 24;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));

  const formatUnit = (value: number, unit: string) =>
    value > 0 ? `${value} ${unit}${value > 1 ? "s" : ""}` : "";

  const parts = [];
  if (days > 0) parts.push(formatUnit(days, "day"));
  if (hours > 0) parts.push(formatUnit(hours, "hour"));
  if (minutes > 0) parts.push(formatUnit(minutes, "minute"));
  if (seconds > 0) parts.push(formatUnit(seconds, "second"));

  const durationString = parts.join(" & ").replace(/ and(?=[^and]*$)/, ", &"); // Format for commas and 'and'
  return `Took ${durationString} to execute.`;
}

export function loopWithRandomInterval(
  array: any[],
  callback: any,
  minInterval = 100,
  maxInterval = 600
) {
  let index = 0;

  function randomDelay(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function processNextElement() {
    if (index < array.length) {
      const chunk = array[index];
      callback(chunk); // Call the provided callback with element, index, and array
      index++;
      console.log(chunk);
      let minInt = minInterval;
      let maxInt = maxInterval;
      if (
        chunk?.message?.content?.content_type === MessageContentType.CONTENT_TYPE_CHAT_RESPONSE ||
        chunk?.message?.content?.content_type === MessageContentType.CONTENT_TYPE_CHAIN_OF_THOUGHT
      ) {
        minInt = 70;
        maxInt = 300;
      }
      setTimeout(processNextElement, randomDelay(minInt, maxInt));
    } else {
      console.log("Completed looping through the array.");
    }
  }

  processNextElement();
}
export function getValueByPath(obj: Record<string, any>, path: string) {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let result = obj;

  for (const key of keys) {
    if (result[key] === undefined) {
      return undefined; // If key is not found, return undefined
    }
    result = result[key];
  }
  return result;
}

export function formatDataAsLabelValue(
  data: any[],
  labelPath: string,
  valuePath: string
): { label: any; value: any }[] {
  if (!Array.isArray(data)) {
    console.error("Input data should be an array.");
    return [];
  }

  return data.map((item) => ({
    label: getValueByPath(item, labelPath),
    value: getValueByPath(item, valuePath),
  }));
}

export const setNestedValue = (obj: any, path: string, value: any) => {
  const keys = path.split(".");
  let current = obj;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value; // Set value at the final key
    } else {
      // Ensure the key exists and is an object
      current[key] = current[key] || {};
      current = current[key];
    }
  });

  return obj;
};

export function isEmptyObject(obj: any): boolean {
  if (obj === null || typeof obj !== "object") {
    return false;
  }
  return Object.keys(obj).length === 0;
}
export function getUrlSearchParams(url: string) {
  const urlParams = new URLSearchParams(new URL(url).search);
  const params: { [key: string]: string } = {};
  for (const [key, value] of urlParams) {
    params[key] = value;
  }
  return params;
}

export function updateUrlParams(params: { [key: string]: string }) {
  const url = new URL(window.location.href);
  for (const key in params) {
    if (Object.hasOwn(params, key)) {
      if (params[key] === null || params[key] === "") {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, params[key]);
      }
    }
  }
  window.history.pushState({}, "", url.toString());
}

export const getRandomNumberInRange = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const isSmallScreen = () => {
  const width =
    window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  return width <= 768; // Typical max width for mobile devices
};

const isMediumScreen = () => {
  const width =
    window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  return width > 768 && width <= 1024; // Typical range for tablets
};

// Updated isMobile function
export const isMobile = () => {
  const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
  const mobileRegex =
    /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
  return mobileRegex.test(userAgent) && isSmallScreen();
};

// Updated isTab function
export const isTab = () => {
  const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
  const tabletRegex =
    /iPad|Tablet|Nexus 7|Nexus 10|Kindle|Silk|PlayBook|SM-T|GT-P|SCH-I800|Xoom|P160U|Tab/i;
  return tabletRegex.test(userAgent) || (isMediumScreen() && !isMobile());
};

// Updated isTabOrMobile function
export const isTabOrMobile = () => {
  return isMobile() || isTab() || isMediumScreen() || isSmallScreen();
};

// Make title case string
export const toTitleCase = (str: string) => {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
};

// Greet based on time
export function greetBasedOnTime(name?: string) {
  //Add ellipses if name length is more than 10
  name = (name?.length ?? 0) > 10 ? name?.slice(0, 10) + "..." : name;
  name = name ? toTitleCase(name) : name;
  const date = new Date();
  const hours = date.getHours();
  if (hours < 12) {
    return "Good morning" + (name ? ", " + name : "") + "!";
  } else if (hours < 18) {
    return "Good afternoon" + (name ? ", " + name : "") + "!";
  } else {
    return "Good evening" + (name ? ", " + name : "") + "!";
  }
}

// Get the first name from full name
export function getFirstName(fullName?: string | null) {
  if (!fullName) return "";
  return fullName.split(" ")[0];
}

// Get the current timestamp for naming files
export const currentTimestamp = () => {
  const date = new Date();
  return `${date.getFullYear()}${addZero(date.getMonth() + 1)}${addZero(date.getDate())}_${addZero(
    date.getHours()
  )}${addZero(date.getMinutes())}${addZero(date.getSeconds())}`;
};

//create a throttle function
export function throttle(this: unknown, func: any, limit: number) {
  let inThrottle: boolean;
  return (...args: any[]) => {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
export const replaceWithEscapeCharacters = (input: string) => {
  return input.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
};

export const removeQuotesFromStartAndEnd = (input: string) => {
  // Remove if starts with \" or ends with \" or both
  return `${input.replace(/^\\"|\\"$/g, "")}`;
};

export function escapeString(str: string): string {
  return str
    .replace(/\\/g, "\\\\") // Escape backslashes
    .replace(/\n/g, "\\n") // Escape new lines
    .replace(/\r/g, "\\r") // Escape carriage returns
    .replace(/\t/g, "\\t") // Escape tabs
    .replace(/"/g, '\\"') // Escape double quotes
    .replace(/'/g, "\\'"); // Escape single quotes
}

export function tableDataModifier(badText: string) {
  return removeQuotesFromStartAndEnd(
    escapeString(`${JSON.stringify(`${badText}`?.replace(/"/g, `'`))}`)
  );
}

export function highlightText(text: string, filters: string[]): string {
  if (!text || filters.length === 0) {
    return text;
  }

  // Escape special regex characters in each filter word.
  const escapedFilters = filters.map((filter) => filter?.replace?.(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  // Create a regex that will match any of the filter words, case-insensitively.
  const regex = new RegExp(`(${escapedFilters.join("|")})`, "gi");

  // Replace each match with a <span> that has a yellow background.
  return text.replace(regex, "<highlight>$1</highlight>");
}

/**
 * Removes empty fields from an object.
 * @param obj The object to clean.
 * @returns The cleaned object.
 */
export function removeEmptyFields<T extends Record<string, any>>(obj2: T): T {
  const obj = structuredClone(obj2);
  Object.keys(obj).forEach((key) => {
    if (obj[key] === "" || obj[key] === null || obj[key] === undefined) {
      delete obj[key];
    }
  });
  return obj;
}

export function mergeArray<T extends Record<string, any>>(
  array1: T[],
  array2: T[],
  uniqueKey: keyof T = "uuid"
): T[] {
  const mergedMap = new Map<string | number, T>();

  // Add elements from array1 first
  for (const item of array1) {
    mergedMap.set(item[uniqueKey], item);
  }

  // Add elements from array2, overwriting existing ones
  for (const item of array2) {
    const existingItem = mergedMap.get(item[uniqueKey]);
    if (existingItem) {
      if (!item?.name) {
        //@ts-ignore - Dynamic property assignment needed for name fallback
        item.name = existingItem.name;
      }
      const updated = { ...existingItem, ...item };
      mergedMap.set(item[uniqueKey], updated);
    } else {
      mergedMap.set(item[uniqueKey], item);
    }
  }

  // Maintain the order of array1 first, then add remaining elements from array2
  const result: T[] = [];
  const seenKeys = new Set<string | number>();

  for (const item of array1) {
    if (mergedMap.has(item[uniqueKey]) && !seenKeys.has(item[uniqueKey])) {
      result.push(mergedMap.get(item[uniqueKey])!);
      seenKeys.add(item[uniqueKey]);
    }
  }

  for (const item of array2) {
    if (!seenKeys.has(item[uniqueKey])) {
      result.push(mergedMap.get(item[uniqueKey])!);
      seenKeys.add(item[uniqueKey]);
    }
  }

  return result;
}

export const getFileName = (url: string): string => {
  const parts = url?.split("/");
  return parts?.[parts.length - 1] ?? "";
};

export const convertSizeWithUnit = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export function convertStringArrayToMarkdownList(array: string[]): string {
  if (!array || array.length === 0) return "";
  return array?.map((item) => `- ${item}`).join("\n");
}

export function formatDefaultMomentDate(m: Moment, isUTC = false, showDay = true): string {
  const now = moment.utc();

  // Calculate the difference in whole days (cloning to avoid mutation)
  const diffDays = m.clone().startOf("day").diff(now.clone().startOf("day"), "days");

  let prefix = "";
  if (showDay) {
    if (diffDays === 0) {
      prefix = "Today";
    } else if (diffDays === -1) {
      prefix = "Yesterday";
    } else if (diffDays === 1) {
      prefix = "Tomorrow";
    } else {
      prefix = m.format("dddd");
    }
  }

  // Guess the current time zone

  // Determine if time details are present (i.e. not all zero)
  const hasTime = m.hour() !== 0 || m.minute() !== 0 || m.second() !== 0;

  // If UTC is specified, use UTC format
  let convertedM = m.clone();
  if (isUTC) {
    convertedM = convertedM.utc();
  } else {
    convertedM = convertedM.tz(moment.tz.guess());
  }

  // Choose format based on whether there's time info
  const formattedDate = hasTime
    ? convertedM.format("Do MMMM YYYY, hh:mm:ss A z")
    : convertedM.format("Do MMMM, YYYY (z)");

  if (!showDay) return formattedDate;
  return `${prefix}, ${formattedDate}`;
}

// Define an array of formats to try when parsing a candidate date string.

export function wrapDatesInTimeTag(text: string) {
  const possibleFormats = [...possibleMomentDateFormats, moment.ISO_8601, moment.HTML5_FMT.DATE];

  // Combine two regex patterns: one for ISO-like dates and one for formal month-name dates.
  const dateRegex = new RegExp(
    [
      // 1. ISO-like dates: e.g. 2025-03-24 or 2025-03-24 10:02:47.060797+00:00 or with abbreviation like "UTC"
      "(?<!<time>)\\b\\d{4}-\\d{2}-\\d{2}(?:[ T]\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?(?:(?:[+-]\\d{2}:\\d{2})|(?:\\s+(?:UTC|PDT))?))?\\b(?!<\\/time>)",
      // 2. Formal dates: e.g. September 17, 2024, at 6:56 PM
      "(?<!<time>)\\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\\s+\\d{1,2},\\s+\\d{4}(?:,\\s*at\\s*\\d{1,2}:\\d{2}\\s*(?:AM|PM))?\\b(?!<\\/time>)",
      //  3. Compact format without seconds: e.g. "20250324 100247 UTC"
      "(?<!<time>)\\b\\d{8}\\s\\d{6}(?:\\s(?:UTC|PDT))?\\b(?!<\\/time>)",
      // 4. Compact format with seconds: e.g. "20250324 1002470607970000"
      "(?<!<time>)\\b\\d{8}\\s\\d{9,}\\b(?!<\\/time>)",
      // 5. TZ format: e.g. "2025-03-24T10:02:47+00:00" or "2025-03-24T10:02:47.060797000Z" or "2025-03-24T10:02:47.060797+00:00"
      "(?<!<time>)\\b\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?(?:Z|[+-]\\d{2}:\\d{2})?\\b(?!<\\/time>)",
      // 6. Unix timestamp: e.g. 1648019367000 or 1648019367
      "(?<!<time>)\\b\\d{10,13}\\b(?!<\\/time>)",
      // 7. Formal date with time: e.g. "March 24, 2025, at 10:02 AM"
      "(?<!<time>)\\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\\s+\\d{1,2},\\s+\\d{4}(?:,\\s*at\\s*\\d{1,2}:\\d{2}\\s*(?:AM|PM))?\\b(?!<\\/time>)",
      // 8. Formal date with time and timezone: e.g. "March 24, 2025, at 10:02 AM UTC"
      "(?<!<time>)\\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\\s+\\d{1,2},\\s+\\d{4}(?:,\\s*at\\s*\\d{1,2}:\\d{2}\\s*(?:AM|PM))?\\s+(?:UTC|PDT)\\b(?!<\\/time>)",
    ].join("|"),
    "gi"
  );
  // The replacer function attempts to parse the matched substring.
  const replacer = (match: any) => {
    // Handle Unix timestamps specifically
    if (/^\d+$/.test(match)) {
      const numericValue = parseInt(match, 10);
      // If length is 10, treat as seconds
      // If length is 13, treat as milliseconds
      // Otherwise, try to normalize to milliseconds
      let timestamp;
      if (match.length === 10) {
        timestamp = numericValue * 1000; // Convert seconds to milliseconds
      } else if (match.length === 13) {
        timestamp = numericValue; // Already in milliseconds
      } else if (match.length > 13) {
        timestamp = Math.floor(numericValue / Math.pow(10, match.length - 13)); // Normalize to milliseconds
      } else {
        timestamp = numericValue * Math.pow(10, 13 - match.length); // Pad to milliseconds
      }
      const m = moment(timestamp);
      if (m.isValid()) {
        return `<time>${match}</time>`;
      }
    }

    // For non-numeric timestamps, try parsing with formats
    let m = moment.utc(match, possibleFormats);
    if (!m.isValid()) {
      m = moment.utc(match);
    }
    if (m.isValid()) {
      return `<time>${match}</time>`;
    }
    return match;
  };

  // Replace all occurrences of date-like strings with our wrapped version.
  return text.replace(dateRegex, replacer);
}

// Function to remove empty objects
export function removeEmptyObjects(obj: any): any {
  if (!obj || typeof obj !== "object") return obj;

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      } else {
        obj[key] = removeEmptyObjects(obj[key]);
        if (Object.keys(obj[key]).length === 0) {
          delete obj[key];
        }
      }
    } else if (Array.isArray(obj[key]) && obj[key].length === 0) {
      delete obj[key];
    } else if (obj[key] === null || obj[key] === undefined || obj[key] === "") {
      delete obj[key];
    }
  });

  return obj;
}

//create a debounce function
export function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null;
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

// Deep compare two objects
export function deepEqual(obj1: any, obj2: any): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

// Direct compare two objects
export function directEqual(obj1: any, obj2: any): boolean {
  // Handle null/undefined cases
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((val, index) => directEqual(val, obj2[index]));
  }

  // Handle objects
  if (typeof obj1 === "object" && typeof obj2 === "object") {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => keys2.includes(key) && directEqual(obj1[key], obj2[key]));
  }

  // Handle primitive values
  return obj1 === obj2;
}

/**
 * Checks if a string is a valid http or https URL
 * @param url - the string to validate
 * @returns true if valid http(s) url, false otherwise
 */
export function isValidHttpUrl(url: string): boolean {
  if (typeof url !== "string" || !url.trim()) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}
